import { ChevronDownIcon } from '@radix-ui/react-icons'
import * as Select from '@radix-ui/react-select'
import clsx from 'clsx'
import * as React from 'react'
import { useCallback } from 'react'
import {
  Country,
  getCountries,
  getCountryCallingCode
} from 'react-phone-number-input'
import enCountryLabels from 'react-phone-number-input/locale/en.json'

import { FLAG_BASE_URL } from './CountryNumberSelect.constants'
import Styles from './CountryNumberSelect.module.scss'

type Props = {
  country?: Country
  onChange: (value: Country) => void
  className?: string
  disabled?: boolean
  dir?: 'rtl' | 'ltr'
}

const CountryNumberSelect = React.memo(
  ({ country, onChange, className, disabled, dir }: Props) => {
    const chosenCountryFlag = country
      ? FLAG_BASE_URL.replace('{COUNTRY_SHORT}', country)
      : ''

    const enhancedOnChange = useCallback(
      (value: Country) => {
        // onChange is triggered on mount of select (so its a workaround for react-hook-form)
        if (!value) return

        onChange(value)
      },
      [onChange]
    )

    return (
      <div className={clsx(Styles.wrapper, className)} data-testid="trigger">
        <Select.Root
          dir={dir}
          disabled={disabled}
          value={country}
          onValueChange={enhancedOnChange}>
          <Select.Trigger
            data-testid="country-trigger"
            className={Styles.trigger}>
            <Select.Value
              className={Styles.placeholder}
              placeholder={<div className={Styles.flagPlaceholder} />}>
              <div className={Styles.placeholder}>
                {chosenCountryFlag && (
                  <img
                    data-testid="country-selector-flag-placeholder"
                    src={chosenCountryFlag}
                    alt={`${country} flag image`}
                  />
                )}
              </div>
            </Select.Value>
            <Select.Icon className={Styles.arrow}>
              <ChevronDownIcon />
            </Select.Icon>
          </Select.Trigger>
          <Select.Content
            position="popper"
            alignOffset={0}
            sideOffset={5}
            className={Styles.content}>
            {getCountries().map(countryShortName => (
              <Select.Item
                key={countryShortName}
                className={Styles.item}
                data-testid={`${countryShortName}-country-option`}
                value={countryShortName}>
                <img
                  src={FLAG_BASE_URL.replace(
                    '{COUNTRY_SHORT}',
                    countryShortName
                  )}
                  alt={`${countryShortName} flag image`}
                />
                <span>{enCountryLabels[countryShortName]}</span>
                <span className={Styles.callingCode} dir="ltr">
                  {`+${getCountryCallingCode(countryShortName)}`}
                </span>
              </Select.Item>
            ))}
            <Select.Separator />
          </Select.Content>
        </Select.Root>
      </div>
    )
  }
)

CountryNumberSelect.displayName = 'CountryNumberSelect'
export default CountryNumberSelect
